import { useState, useRef } from "react";
import { FileText, Upload, Camera, X, Loader2, Check, AlertCircle, Plus, Trash2, AlertTriangle, Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API, authFetch } from "@/App";
import { toast } from "sonner";

export default function DocumentUploader({ 
  petId, 
  petName,
  open, 
  onClose, 
  onSuccess,
  allowSmartUpload = false, // When true, allows upload without pet using smart detection
  useBackgroundProcessing = true // Use async background processing for OCR
}) {
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]); // Array of {file, preview, status, extractedData}
  const [step, setStep] = useState("select"); // select, preview, uploading, result, background-started
  const [docType, setDocType] = useState("libretto");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [pendingUpload, setPendingUpload] = useState(null);
  const [backgroundJobs, setBackgroundJobs] = useState([]); // Track started background jobs
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const resetState = () => {
    setSelectedFiles([]);
    setStep("select");
    setDocType("libretto");
    setUploadProgress(0);
    setDuplicateInfo(null);
    setPendingUpload(null);
    setBackgroundJobs([]);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  // Generate auto title from extracted data or file name
  const generateAutoTitle = (extractedData, fileName) => {
    if (extractedData && !extractedData.error) {
      // Try to generate title from extracted data
      if (extractedData.dati_animale?.nome) {
        const petNameFromDoc = extractedData.dati_animale.nome;
        if (extractedData.vaccini && extractedData.vaccini.length > 0) {
          return `Vaccini ${petNameFromDoc}`;
        }
        if (extractedData.dati_proprietario) {
          return `Libretto ${petNameFromDoc}`;
        }
        return `Documento ${petNameFromDoc}`;
      }
      if (extractedData.tipo_documento) {
        return extractedData.tipo_documento;
      }
    }
    // Fallback to cleaned filename
    const baseName = fileName.replace(/\.[^/.]+$/, "").replace(/[-_]/g, " ");
    return baseName.charAt(0).toUpperCase() + baseName.slice(1);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
    const validFiles = [];
    
    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name}: Formato non supportato`);
        continue;
      }
      if (file.size > 20 * 1024 * 1024) {
        toast.error(`${file.name}: File troppo grande (max 20MB)`);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    // Create file entries with previews
    const newFiles = validFiles.map(file => {
      const entry = {
        id: Math.random().toString(36).substr(2, 9),
        file,
        preview: null,
        status: 'pending', // pending, uploading, success, error
        extractedData: null,
        autoTitle: null
      };

      // Generate preview for images
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setSelectedFiles(prev => prev.map(f => 
            f.id === entry.id ? { ...f, preview: e.target.result } : f
          ));
        };
        reader.readAsDataURL(file);
      }

      return entry;
    });

    setSelectedFiles(prev => [...prev, ...newFiles]);
    setStep("preview");
  };

  const removeFile = (id) => {
    setSelectedFiles(prev => prev.filter(f => f.id !== id));
    if (selectedFiles.length <= 1) {
      setStep("select");
    }
  };

  const handleUploadAll = async () => {
    // Allow upload without petId if smart upload is enabled
    if (selectedFiles.length === 0) return;
    if (!petId && !allowSmartUpload) return;

    setUploading(true);
    setStep("uploading");
    
    let successCount = 0;
    let errorCount = 0;
    const startedJobs = [];

    for (let i = 0; i < selectedFiles.length; i++) {
      const fileEntry = selectedFiles[i];
      setUploadProgress(Math.round(((i) / selectedFiles.length) * 100));
      
      // Update status to uploading
      setSelectedFiles(prev => prev.map(f => 
        f.id === fileEntry.id ? { ...f, status: 'uploading' } : f
      ));

      try {
        const formData = new FormData();
        formData.append("file", fileEntry.file);
        formData.append("title", fileEntry.file.name);
        formData.append("doc_type", docType);

        // Determine which endpoint to use
        let endpoint;
        const isPdf = fileEntry.file.type === "application/pdf";
        
        if (useBackgroundProcessing && petId && isPdf) {
          // Use background processing for PDFs
          endpoint = `${API}/jobs/upload-document`;
          formData.append("pet_id", petId);
        } else if (petId) {
          // Use standard upload for images or when background is disabled
          endpoint = `${API}/upload/document`;
          formData.append("pet_id", petId);
          formData.append("extract_data", "true");
        } else {
          // Smart upload without pet
          endpoint = `${API}/upload/smart-document`;
          formData.append("extract_data", "true");
        }

        // Create AbortController for timeout (shorter for background jobs)
        const controller = new AbortController();
        const timeoutMs = useBackgroundProcessing && isPdf ? 30000 : 180000;
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

        const response = await authFetch(endpoint, {
          method: "POST",
          body: formData,
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);

        if (response.ok) {
          const data = await response.json();
          
          // Check if this was a background job
          if (data.job_id) {
            startedJobs.push({
              job_id: data.job_id,
              document_id: data.document_id,
              filename: fileEntry.file.name
            });
            
            setSelectedFiles(prev => prev.map(f => 
              f.id === fileEntry.id ? { 
                ...f, 
                status: 'background',
                jobId: data.job_id,
                documentId: data.document_id
              } : f
            ));
          } else {
            // Standard upload completed
            const autoTitle = generateAutoTitle(data.extracted_data, fileEntry.file.name);
            
            if (data.document_id && autoTitle !== fileEntry.file.name) {
              try {
                await authFetch(`${API}/documents/${data.document_id}`, {
                  method: "PATCH",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ title: autoTitle })
                });
              } catch (e) {
                console.log("Could not update title:", e);
              }
            }

            setSelectedFiles(prev => prev.map(f => 
              f.id === fileEntry.id ? { 
                ...f, 
                status: 'success', 
                extractedData: data.extracted_data,
                autoTitle 
              } : f
            ));
          }
          successCount++;
        } else {
          const error = await response.json();
          setSelectedFiles(prev => prev.map(f => 
            f.id === fileEntry.id ? { ...f, status: 'error', errorMsg: error.detail } : f
          ));
          errorCount++;
        }
      } catch (error) {
        let errorMsg = 'Errore di connessione';
        let errorType = 'connection';
        
        if (error.name === 'AbortError') {
          errorMsg = 'Timeout - Il file è troppo grande. Riprova con un file più piccolo.';
          errorType = 'timeout';
        } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
          errorMsg = 'Errore di rete. Controlla la connessione e riprova.';
          errorType = 'connection';
        }
        
        setSelectedFiles(prev => prev.map(f => 
          f.id === fileEntry.id ? { ...f, status: 'error', errorMsg, errorType } : f
        ));
        errorCount++;
      }
    }

    setUploadProgress(100);
    setUploading(false);
    setBackgroundJobs(startedJobs);

    // Determine which result screen to show
    if (startedJobs.length > 0) {
      setStep("background-started");
      toast.success(
        <div className="flex flex-col">
          <span className="font-semibold">{startedJobs.length} document{startedJobs.length > 1 ? 'i' : 'o'} in elaborazione</span>
          <span className="text-sm opacity-80">Puoi chiudere e continuare a navigare</span>
        </div>,
        { duration: 4000 }
      );
    } else {
      setStep("result");
      if (successCount > 0 && errorCount === 0) {
        toast.success(`${successCount} document${successCount > 1 ? 'i caricati' : 'o caricato'} con successo!`);
      } else if (successCount > 0 && errorCount > 0) {
        toast.warning(`${successCount} caricati, ${errorCount} falliti`);
      } else {
        toast.error("Errore nel caricamento");
      }
    }

    if (successCount > 0) {
      onSuccess?.();
    }
  };

  const docTypes = [
    { value: "libretto", label: "Libretto Sanitario" },
    { value: "passaporto", label: "Passaporto Animale" },
    { value: "vaccination_cert", label: "Certificato Vaccini" },
    { value: "esami", label: "Esami del Sangue" },
    { value: "other", label: "Altro" }
  ];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[520px] rounded-3xl bg-white dark:bg-[#1C1C1E] p-0 border-0 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-6 text-white">
          <DialogTitle className="text-xl font-bold flex items-center gap-3">
            <FileText size={24} />
            Carica Documenti
          </DialogTitle>
          <p className="text-blue-100 text-sm mt-1">
            {petName ? `Per ${petName}` : allowSmartUpload ? "L'AI identificherà l'animale automaticamente" : "Carica uno o più documenti"}
          </p>
        </div>

        <div className="p-6 max-h-[calc(90vh-120px)] overflow-y-auto">
          {/* Smart Upload Notice */}
          {!petId && allowSmartUpload && step === "select" && (
            <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="font-medium text-amber-800 dark:text-amber-300 text-sm">Upload Intelligente</p>
                  <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                    Non hai animali registrati. Carica un documento e l'AI estrarrà automaticamente i dati 
                    per creare il profilo dell'animale.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step: Select Files */}
          {step === "select" && (
            <div className="space-y-6">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                onChange={handleFileSelect}
                className="hidden"
                multiple
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
              />

              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => cameraInputRef.current?.click()}
                  className="flex flex-col items-center justify-center gap-3 p-6 rounded-2xl bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-dashed border-orange-200 hover:border-orange-400 transition-colors"
                >
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center">
                    <Camera className="text-white" size={28} />
                  </div>
                  <span className="font-medium text-gray-700">Scatta foto</span>
                </button>

                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex flex-col items-center justify-center gap-3 p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-dashed border-blue-200 hover:border-blue-400 transition-colors"
                >
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center">
                    <Upload className="text-white" size={28} />
                  </div>
                  <span className="font-medium text-gray-700">Carica file</span>
                </button>
              </div>

              <p className="text-center text-sm text-gray-500">
                Supporta: JPG, PNG, WEBP, PDF (max 20MB) • Puoi selezionare più file
              </p>

              {/* Doc Type Selection */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-3">Tipo di documento</p>
                <div className="flex flex-wrap gap-2">
                  {docTypes.map(type => (
                    <button
                      key={type.value}
                      onClick={() => setDocType(type.value)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        docType === type.value
                          ? 'bg-[#1D1D1F] text-white shadow-lg'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step: Preview Files */}
          {step === "preview" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-gray-800">
                  {selectedFiles.length} file selezionat{selectedFiles.length > 1 ? 'i' : 'o'}
                </h3>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                >
                  <Plus size={16} /> Aggiungi altri
                </button>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                onChange={handleFileSelect}
                className="hidden"
                multiple
              />

              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {selectedFiles.map(f => (
                  <div key={f.id} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
                    {/* Preview */}
                    <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
                      {f.preview ? (
                        <img src={f.preview} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <FileText className="text-gray-400" size={24} />
                        </div>
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 truncate">{f.file.name}</p>
                      <p className="text-sm text-gray-500">
                        {(f.file.size / 1024 / 1024).toFixed(2)} MB • {f.file.type.split('/')[1].toUpperCase()}
                      </p>
                    </div>
                    
                    {/* Remove button */}
                    <button
                      onClick={() => removeFile(f.id)}
                      className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => { setSelectedFiles([]); setStep("select"); }}
                  className="flex-1 h-12 rounded-xl"
                >
                  Annulla
                </Button>
                <Button
                  onClick={handleUploadAll}
                  className="flex-1 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 rounded-xl"
                >
                  <Upload size={18} className="mr-2" />
                  Carica {selectedFiles.length > 1 ? 'tutti' : ''}
                </Button>
              </div>
            </div>
          )}

          {/* Step: Uploading */}
          {step === "uploading" && (
            <div className="space-y-6 py-8">
              <div className="flex flex-col items-center">
                <Loader2 className="animate-spin text-blue-500 mb-4" size={48} />
                <p className="font-semibold text-gray-800">Caricamento in corso...</p>
                <p className="text-sm text-gray-500 mt-1">Analisi OCR dei documenti</p>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>

              {/* File status list */}
              <div className="space-y-2">
                {selectedFiles.map(f => (
                  <div key={f.id} className="flex items-center gap-3 p-2 rounded-lg bg-gray-50">
                    <div className="w-8 h-8 flex items-center justify-center">
                      {f.status === 'pending' && <div className="w-3 h-3 rounded-full bg-gray-300" />}
                      {f.status === 'uploading' && <Loader2 className="animate-spin text-blue-500" size={18} />}
                      {f.status === 'success' && <Check className="text-green-500" size={18} />}
                      {f.status === 'error' && <AlertCircle className="text-red-500" size={18} />}
                    </div>
                    <span className="text-sm text-gray-600 truncate flex-1">{f.file.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step: Results */}
          {step === "result" && (
            <div className="space-y-4">
              <div className="flex items-center justify-center gap-2 py-4">
                {selectedFiles.some(f => f.status === 'error') ? (
                  <>
                    <AlertTriangle className="text-amber-500" size={32} />
                    <h3 className="text-xl font-bold text-gray-800">Completato con errori</h3>
                  </>
                ) : (
                  <>
                    <Check className="text-green-500" size={32} />
                    <h3 className="text-xl font-bold text-gray-800">Completato!</h3>
                  </>
                )}
              </div>

              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {selectedFiles.map(f => (
                  <div key={f.id} className={`p-4 rounded-xl border ${
                    f.status === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex items-center gap-3">
                      {f.status === 'success' ? (
                        <Check className="text-green-600 flex-shrink-0" size={20} />
                      ) : (
                        <AlertCircle className="text-red-600 flex-shrink-0" size={20} />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-800 truncate">
                          {f.autoTitle || f.file.name}
                        </p>
                        {f.status === 'success' && f.extractedData && !f.extractedData.error && (
                          <p className="text-sm text-green-600">Dati estratti con successo</p>
                        )}
                        {f.status === 'error' && (
                          <div>
                            <p className="text-sm text-red-600">{f.errorMsg || 'Errore nel caricamento'}</p>
                            {f.errorType === 'timeout' && (
                              <p className="text-xs text-red-500 mt-1">
                                Prova a comprimere il PDF o caricarlo come immagini separate.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                      {f.status === 'error' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            // Reset this file and go back to preview to retry
                            setSelectedFiles(prev => prev.map(file => 
                              file.id === f.id ? { ...file, status: 'pending', errorMsg: null, errorType: null } : file
                            ));
                            setStep("preview");
                          }}
                          className="flex-shrink-0 text-red-600 border-red-300 hover:bg-red-50"
                        >
                          Riprova
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <Button
                onClick={handleClose}
                className="w-full h-12 bg-[#1D1D1F] hover:bg-[#2D2D2F] rounded-xl mt-4"
              >
                Chiudi
              </Button>
            </div>
          )}

          {/* Step: Background Jobs Started */}
          {step === "background-started" && (
            <div className="space-y-6 py-4">
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mb-4">
                  <Rocket className="text-white" size={32} />
                </div>
                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">
                  Elaborazione avviata!
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mt-2 max-w-sm">
                  I tuoi documenti verranno elaborati in background. 
                  Puoi chiudere questa finestra e continuare a navigare.
                </p>
              </div>

              {/* Background jobs list */}
              <div className="space-y-2">
                {selectedFiles.filter(f => f.status === 'background').map(f => (
                  <div key={f.id} className="flex items-center gap-3 p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                    <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-800 flex items-center justify-center">
                      <Loader2 className="text-blue-500 animate-spin" size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 dark:text-gray-200 text-sm truncate">
                        {f.file.name}
                      </p>
                      <p className="text-xs text-blue-600 dark:text-blue-400">
                        In elaborazione...
                      </p>
                    </div>
                  </div>
                ))}

                {/* Show any immediate successes */}
                {selectedFiles.filter(f => f.status === 'success').map(f => (
                  <div key={f.id} className="flex items-center gap-3 p-3 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                    <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center">
                      <Check className="text-green-500" size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 dark:text-gray-200 text-sm truncate">
                        {f.autoTitle || f.file.name}
                      </p>
                      <p className="text-xs text-green-600 dark:text-green-400">
                        Completato
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  <strong>Nota:</strong> Riceverai una notifica quando l'elaborazione sarà completata. 
                  I vaccini estratti appariranno automaticamente nel profilo del tuo animale.
                </p>
              </div>

              <Button
                onClick={handleClose}
                className="w-full h-12 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 rounded-xl"
              >
                Continua a navigare
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
